import { useEffect } from 'react';

import { pushToDataLayer } from '../../../../services/gtmService';
import { useBraze } from '../../../../contexts/BrazeContext';
import { SaveAd } from '../../../../components/Toolkit/SaveAd/SaveAd';
import { accountsApi } from '../../../../api/accountsApi';
import { fireToast } from '../../../../helpers/Toasts';
import StatusCode from 'status-code-enum';
import { useUserContext } from '../../../../contexts/UserContext';
import { formatAuthorizationHeader } from '../../../../helpers/auth';
import { AUTHENTICATION_STATUSES } from '../../AdDetails/types';
import {
  isCarsSection,
  isElectric,
  isOver20k,
} from '../../AdDetails/hooks/ListingViewBrazeTracking/ListingViewBrazeTracking.constants';

interface ShowAndToggleSaveAdProps {
  adId: number;
  isViewingOwnAd?: boolean;
  userSaved?: boolean;
  isUnderlined?: boolean;
  isAdSaved?: boolean;
  className?: string;
  setIsAdSaved?: (t: boolean) => void;
  isPreview?: boolean;
  subsection?: string;
  sellerType?: string;
  isFreeHistoryCheckAd?: boolean;
  category?: string;
  location?: string;
  priceEuro?: number | null;
  fuelType?: string | null;
}

function ShowAndToggleSaveAd({
  adId,
  isViewingOwnAd = false,
  isUnderlined = false,
  isAdSaved,
  setIsAdSaved,
  className,
  isPreview,
  subsection,
  sellerType,
  isFreeHistoryCheckAd = false,
  category,
  location,
  priceEuro,
  fuelType,
}: ShowAndToggleSaveAdProps) {
  const { handleLogin, accessToken, status } = useUserContext();
  const {
    logBrazeSaveAdEvent,
    logBrazeFreeHistoryCheckAdSave,
    logBrazeSaveAd20kEvent,
    logBrazeSaveAdEvEvent,
  } = useBraze();

  useEffect(() => {
    if (status === AUTHENTICATION_STATUSES.UNAUTHENTICATED && isAdSaved) {
      setIsAdSaved && setIsAdSaved(false);
    }
    // TODO: Clean up this effect's dependencies. We're disabling this lint error for now so we can
    // clean up the lint logs. Ideally we should rewrite this code to be less error prone and trust
    // the lint rule's judgement.
    // https://distilledsch.tpondemand.com/RestUI/Board.aspx#page=userstory/98606
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const handleUserEvent = () => {
    status === AUTHENTICATION_STATUSES.AUTHENTICATED
      ? triggerToggleSave()
      : handleLogin();
  };

  const triggerToggleSave = async () => {
    const res = isAdSaved !== undefined && (await handleRequest(isAdSaved));
    if (res) {
      setIsAdSaved && setIsAdSaved(!isAdSaved);

      if (!isAdSaved) {
        pushToDataLayer({
          event: 'create_saved_ad',
        });

        if (isFreeHistoryCheckAd) {
          logBrazeFreeHistoryCheckAdSave({
            ad_id: adId.toString(),
            category: category ?? '',
            location: location ?? '',
            ad_price: priceEuro?.toString() ?? '',
          });
        } else {
          logBrazeSaveAdEvent(subsection, sellerType);
        }

        if (isCarsSection(category)) {
          const carAdEventInput = {
            ad_id: adId.toString(),
            ad_price: priceEuro?.toString() ?? '',
            seller_type: sellerType ?? '',
          };

          if (isOver20k(priceEuro)) {
            logBrazeSaveAd20kEvent(carAdEventInput);
          }

          if (isElectric(fuelType)) {
            logBrazeSaveAdEvEvent(carAdEventInput);
          }
        }
      } else {
        pushToDataLayer({
          event: 'delete_saved_ad',
        });
      }
    }
  };

  const handleRequest = async (savedStatus: boolean) => {
    try {
      const { status } = savedStatus
        ? await accountsApi.unsaveAd(
            adId,
            formatAuthorizationHeader(accessToken),
          )
        : await accountsApi.saveAd(
            adId,
            formatAuthorizationHeader(accessToken),
          );
      const requestStatus = status === StatusCode.SuccessOK;
      if (requestStatus) {
        if (!savedStatus) {
          fireToast({
            type: 'SUCCESS',
            text: 'Added to your Saved Ads',
          });
        } else {
          fireToast({
            type: 'SUCCESS',
            text: 'Removed from your Saved Ads',
          });
        }
      }
      return requestStatus;
    } catch (_error) {
      fireToast({
        type: 'ERROR',
        text: 'Oops! Something went wrong, please try again later',
      });
      return false;
    }
  };

  return !isViewingOwnAd ? (
    <SaveAd
      toggleSave={!isPreview ? handleUserEvent : undefined}
      isAdSaved={isAdSaved}
      isUnderlined={isUnderlined}
      className={className}
      iconSizeDesktop="SM"
    />
  ) : null;
}

export { ShowAndToggleSaveAd };
